/**
 * Compact payment summary for Guided Note (PRD.161 Section 7.8).
 * Shows total charged / paid / balance at a glance.
 * Payments is a placeholder in v1 — this provides a useful summary
 * until the full payments module is built.
 */

import { useMemo } from 'react';
import { usePowerSyncQuery } from '../../../adapters/powersync';
import { qInvoicesByEncounter, type InvoiceRow } from '@helios/data-access';

// ─── Helpers ───

function fmtCurrency(amount: number): string {
  return `₱${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
}

// ─── Props ───

interface Props {
  patientId: string;
  encounterId: string;
}

// ─── Component ───

export function GuidedPaymentSummary({ patientId, encounterId }: Props) {
  const { data: invoiceRows = [] } = usePowerSyncQuery<InvoiceRow>(
    qInvoicesByEncounter(patientId, encounterId),
  );

  const { totalCharged, totalPaid, balance } = useMemo(() => {
    let charged = 0;
    let paid = 0;
    for (const inv of invoiceRows) {
      charged += inv.grand_total ?? 0;
      if (inv.status === 'paid') paid += inv.grand_total ?? 0;
    }
    return { totalCharged: charged / 100, totalPaid: paid / 100, balance: (charged - paid) / 100 };
  }, [invoiceRows]);

  if (invoiceRows.length === 0) {
    return (
      <div
        style={{
          padding: 'var(--sp-5) var(--sp-6)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
          fontStyle: 'italic',
        }}
      >
        No payment activity for this visit
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 'var(--sp-4) var(--sp-6)',
        display: 'flex',
        gap: 'var(--sp-5)',
        flexWrap: 'wrap',
      }}
    >
      <SummaryItem label="Charged" value={fmtCurrency(totalCharged)} />
      <SummaryItem label="Paid" value={fmtCurrency(totalPaid)} color="var(--color-success-text)" />
      <SummaryItem
        label="Balance"
        value={fmtCurrency(balance)}
        color={balance > 0 ? 'var(--color-warning-text)' : 'var(--text)'}
      />
    </div>
  );
}

function SummaryItem({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>{label}</span>
      <span
        style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, color: color ?? 'var(--text)' }}
      >
        {value}
      </span>
    </div>
  );
}
